import type { ReactNode } from 'react'
import { DataTable } from './DataTable'
import { cx } from '@/lib/cx'
import { useMinWidth } from '@/lib/useMediaQuery'

/**
 * One list of records, in the shape the screen it is on can actually read.
 *
 * From `md` up it is a table, because a table is what six columns of clinic
 * data are for. Below `md` it is a list of cards — not the same table scrolled
 * sideways. A doctor holding a phone cannot compare columns that are off the
 * edge of the screen, and the horizontal scroll hides exactly the column the
 * row exists for: a booked appointment whose "When" you have to swipe to reach
 * is a booking you will misread.
 *
 * Both renderings come from one set of column definitions, so a column cannot
 * be added to the desktop table and forgotten on the phone.
 */

export interface RecordColumn<Row> {
  /** Stable identity for React and for the card's label. */
  key: string
  /** The table's column heading, and the label the card puts beside the value. */
  header: string
  /**
   * The cell's content. Returning `null` means "this record has nothing here":
   * the table still rules a cell and prints an em dash so the grid stays
   * aligned, and the card leaves the line out entirely rather than stacking
   * four dashes down a phone screen.
   */
  cell: (row: Row) => ReactNode
  /**
   * The one column that identifies the record — the patient, the medicine, the
   * test. On the card it is the title and carries no label, because "Patient:
   * Meena Ali" is a label nobody needs. Exactly one column should set it.
   */
  primary?: boolean
  /** A control rather than a fact. On the card these sit together at the end. */
  action?: boolean
  /** Applied to the `<td>` only — the card lays its own values out. */
  className?: string
}

export interface RecordGroup<Row> {
  id: string
  /** Spans the table above its rows; sits above the cards as a subhead. */
  heading: ReactNode
  rows: Row[]
}

interface Props<Row> {
  columns: RecordColumn<Row>[]
  /** One flat run of records, or runs under their own headings. Pass one. */
  rows?: Row[]
  groups?: RecordGroup<Row>[]
  rowKey: (row: Row, index: number) => string
  /**
   * What this list is, for a screen reader arriving at it — "5 appointments".
   * The accessible name of the table and of the card list.
   *
   * A name, not a rendered `<caption>`: every one of these sits inside a `Card`
   * whose title already says the same thing, and a caption would put that
   * sentence into the page twice.
   */
  caption: string
}

export function RecordTable<Row>({ columns, rows, groups, rowKey, caption }: Props<Row>) {
  // The markup differs, so this is a real branch rather than a `md:` class.
  const wide = useMinWidth('md')
  const runs: RecordGroup<Row>[] = groups ?? [{ id: 'all', heading: null, rows: rows ?? [] }]

  return wide ? (
    <DataTable label={caption}>
      <thead>
        <tr>
          {columns.map((column) => (
            <th key={column.key} scope="col">
              {/* An actions column is headed for layout, not for reading. */}
              {column.action ? <span className="sr-only">{column.header}</span> : column.header}
            </th>
          ))}
        </tr>
      </thead>
      {runs.map((run) => (
        <tbody key={run.id}>
          {run.heading !== null && (
            <tr>
              <th
                scope="colgroup"
                colSpan={columns.length}
                className="border-t border-line-soft bg-subtle px-4 py-2 text-left text-xs font-normal"
              >
                {run.heading}
              </th>
            </tr>
          )}
          {run.rows.map((row, index) => (
            <tr key={rowKey(row, index)}>
              {columns.map((column) => (
                <td key={column.key} className={column.className}>
                  {column.cell(row) ?? '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      ))}
    </DataTable>
  ) : (
    <div>
      {runs.map((run) => (
        <section key={run.id} aria-label={typeof run.heading === 'string' ? run.heading : undefined}>
          {run.heading !== null && (
            <p className="border-t border-line-soft bg-subtle px-4 py-2 text-xs text-fg-muted">
              {run.heading}
            </p>
          )}
          <ul
            className="m-0 list-none p-0 [&>li+li]:border-t [&>li+li]:border-line-soft"
            aria-label={run.heading === null ? caption : undefined}
          >
            {run.rows.map((row, index) => (
              <RecordCard
                key={rowKey(row, index)}
                columns={columns}
                row={row}
                // Only the first card of the first run needs the rule above it
                // — the group heading brings its own.
                separated={run.heading === null && index === 0}
              />
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}

/** Title, then the facts as a definition list, then whatever you can do to it. */
function RecordCard<Row>({
  columns,
  row,
  separated,
}: {
  columns: RecordColumn<Row>[]
  row: Row
  separated: boolean
}) {
  const primary = columns.find((column) => column.primary)
  const actions = columns.filter((column) => column.action)
  // An empty value is a line the card does not draw. The table keeps its dash
  // because a blank cell in a grid reads as a rendering fault; a card has no
  // grid to keep aligned.
  const facts = columns
    .filter((column) => !column.primary && !column.action)
    .map((column) => ({ column, value: column.cell(row) }))
    .filter((fact) => fact.value !== null && fact.value !== undefined)

  return (
    <li className={cx('flex flex-col gap-2 px-4 py-3', separated && 'border-t border-line-soft')}>
      {primary && <div className="min-w-0 text-base font-semibold text-fg">{primary.cell(row)}</div>}

      {facts.length > 0 && (
        // Two columns of label above value: a 13px label and its value on one
        // line is how a phone card stays four lines tall instead of eight.
        <dl className="m-0 grid grid-cols-2 gap-x-4 gap-y-2 min-[26rem]:grid-cols-3">
          {facts.map(({ column, value }) => (
            <div key={column.key} className="min-w-0">
              <dt className="text-2xs font-semibold tracking-wide text-fg-subtle uppercase">
                {column.header}
              </dt>
              <dd className="m-0 text-sm text-fg">{value}</dd>
            </div>
          ))}
        </dl>
      )}

      {actions.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {actions.map((column) => (
            <span key={column.key}>{column.cell(row)}</span>
          ))}
        </div>
      )}
    </li>
  )
}
